import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Hammer, Power, Square, Unplug } from "lucide-react";

import {
  deregisterRunner,
  getFleet,
  getFleetCost,
  listPoolProposals,
  listRegisteredRunners,
  proposePools,
  rebuildImage,
  stopExecution,
  terminateInstance,
  type DesiredPool,
  type FleetExecution,
  type FleetImage,
  type FleetInstance,
  type PoolDrift,
  type PoolProposal,
  type PoolState,
  type RegisteredRunner,
} from "@/api/fleet";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Checkbox } from "@/components/form";
import { MenuItem } from "@/components/KebabMenu";
import { PoolEditor } from "@/components/PoolEditor";
import { RelativeTime } from "@/components/RelativeTime";
import { ErrorBox, MutationError } from "@/components/Spinner";
import {
  Button,
  CardSkeleton,
  EmptyState,
  LoadingRegion,
  Notice,
  Page,
  PageHeader,
  StatusPill,
  Table,
  type TableColumn,
  type Tone,
} from "@/components/ui";
import { FACTORY } from "@/lib/sections";
import { isProductionPlane } from "@/runtimeConfig";

// Un runner est éphémère et ne sert qu'un seul job. Passé cette durée, il ne
// travaille probablement plus : c'est la signature des cinq instances restées
// en vol le 21/07/2026 après une boucle de réessai, que seul le budget AWS
// avait fini par signaler, huit heures plus tard.
const SUSPICIOUS_AGE_SECONDS = 2 * 3600;

// L'ambre et l'orange que la page distinguait n'existent plus : « Deployed but
// not declared » est un écart qui demande un regard, comme « Deployment
// pending » — le libellé porte la nuance, le ton le niveau d'alerte.
const POOL_STATES: Record<PoolState, { tone: Tone; label: string }> = {
  in_sync: { tone: "success", label: "In sync" },
  drifted: { tone: "warning", label: "Deployment pending" },
  desired_only: { tone: "info", label: "Never deployed" },
  live_only: { tone: "warning", label: "Deployed but not declared" },
};

function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: "warn";
}) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div
        className={`mt-1 text-3xl font-semibold ${
          tone === "warn" ? "text-warning-fg" : "text-ink"
        }`}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-ink-muted">{hint}</div>}
    </div>
  );
}

function formatAge(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours} h ${String(minutes).padStart(2, "0")}`;
}

function describePool(pool: { instanceType: string; storageGib: number | null }) {
  return `${pool.instanceType} · ${pool.storageGib ?? "?"} GiB`;
}

function isSuspicious(instance: FleetInstance): boolean {
  return instance.ageSeconds !== null && instance.ageSeconds > SUSPICIOUS_AGE_SECONDS;
}

const INSTANCE_COLUMNS: TableColumn<FleetInstance>[] = [
  { key: "instance", header: "Instance", mono: true, cell: (instance) => instance.instanceId },
  { key: "pool", header: "Pool", cell: (instance) => instance.pool ?? "—" },
  { key: "type", header: "Type", cell: (instance) => instance.instanceType ?? "—" },
  {
    key: "market",
    header: "Market",
    cell: (instance) =>
      instance.spot ? <span className="text-ink-muted">spot</span> : <span>on-demand</span>,
  },
  { key: "zone", header: "Zone", muted: true, cell: (instance) => instance.availabilityZone ?? "—" },
  { key: "state", header: "State", cell: (instance) => instance.state ?? "—" },
  {
    key: "age",
    header: "Age",
    align: "right",
    cell: (instance) =>
      isSuspicious(instance) ? (
        <span
          className="font-semibold text-warning-fg"
          title="A runner only serves one job — at this age, it's probably no longer working."
        >
          {formatAge(instance.ageSeconds)}
        </span>
      ) : (
        formatAge(instance.ageSeconds)
      ),
  },
];

const EXECUTION_COLUMNS: TableColumn<FleetExecution>[] = [
  { key: "name", header: "Execution", mono: true, cell: (execution) => execution.name },
  { key: "started", header: "Started", muted: true, cell: (execution) => <RelativeTime epochMs={execution.startedAt} /> },
];

const FAILURE_COLUMNS: TableColumn<FleetExecution>[] = [
  { key: "name", header: "Execution", mono: true, cell: (execution) => execution.name },
  { key: "status", header: "Status", cell: (execution) => execution.status },
  {
    key: "started",
    header: "Started",
    align: "right",
    muted: true,
    cell: (execution) => <RelativeTime epochMs={execution.startedAt} />,
  },
];

// An execution or an image may come back without a name: the position stands in for it, so two nameless
// rows never share a key (the table also makes a repeated key unique, but a stable one is better).
const executionKey = (execution: FleetExecution, index: number) => execution.name ?? `unnamed-${index}`;

const GHOST_COLUMNS: TableColumn<RegisteredRunner>[] = [
  { key: "name", header: "Runner", mono: true, cell: (runner) => runner.name },
  { key: "repo", header: "Repository", muted: true, cell: (runner) => runner.repo },
  { key: "labels", header: "Labels", muted: true, cell: (runner) => runner.labels.join(", ") },
];

const IMAGE_COLUMNS: TableColumn<FleetImage>[] = [
  { key: "name", header: "Pipeline", mono: true, cell: (image) => image.name },
  { key: "status", header: "State", cell: (image) => image.status ?? "—" },
  { key: "lastRun", header: "Last run", muted: true, cell: (image) => image.lastRun ?? "—" },
  { key: "nextRun", header: "Next run", muted: true, cell: (image) => image.nextRun ?? "—" },
];

/** A row of the pools table: a declared/deployed pool, or one only a proposal declares. */
type PoolTableRow =
  | { kind: "drift"; drift: PoolDrift; proposed: DesiredPool | null }
  | { kind: "proposed"; pool: DesiredPool };

function poolColumns(showProposed: boolean): TableColumn<PoolTableRow>[] {
  return [
    {
      key: "label",
      header: "Label",
      cell: (row) => <span className="font-medium">{row.kind === "drift" ? row.drift.label : row.pool.label}</span>,
    },
    {
      key: "desired",
      header: "Desired",
      cell: (row) => (row.kind === "drift" && row.drift.desired ? describePool(row.drift.desired) : "—"),
    },
    {
      key: "deployed",
      header: "Deployed",
      cell: (row) => (row.kind === "drift" && row.drift.live ? describePool(row.drift.live) : "—"),
    },
    ...(showProposed
      ? [
          {
            key: "proposed",
            header: "Proposed",
            cell: (row: PoolTableRow) => {
              if (row.kind === "proposed") return <span className="text-info-fg">{describePool(row.pool)}</span>;
              const { drift, proposed } = row;
              const shown = drift.desired ?? null;
              // Ce qui est proposé n'est signalé que s'il diffère de ce qui est
              // déjà déclaré : une PR qui touche un autre pool ne doit pas faire
              // clignoter toutes les lignes.
              const differs =
                proposed !== null &&
                (shown === null ||
                  proposed.instanceType !== shown.instanceType ||
                  proposed.storageGib !== shown.storageGib ||
                  proposed.spot !== shown.spot);
              // Une proposition porte le fichier ENTIER : une étiquette absente
              // n'y est pas « inchangée », elle est supprimée. Afficher « — »
              // dans ce cas rendrait invisible le seul changement qui puisse
              // mettre des jobs en attente pendant 24 h.
              if (proposed === null) return <span className="text-warning-fg">removed</span>;
              if (differs) return <span className="text-info-fg">{describePool(proposed)}</span>;
              return <span className="text-ink-muted">—</span>;
            },
          },
        ]
      : []),
    {
      key: "state",
      header: "State",
      cell: (row) => {
        if (row.kind === "proposed") return <span className="text-xs text-info-fg">new pool</span>;
        const { drift } = row;
        // A state the table does not know yet shows as itself, neutral (see StatusBadge).
        const style = POOL_STATES[drift.state] ?? { tone: "neutral" as Tone, label: drift.state };
        return (
          <>
            <StatusPill tone={style.tone}>{style.label}</StatusPill>
            {drift.differences.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs text-warning-fg">
                {drift.differences.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </>
        );
      },
    },
    {
      key: "usage",
      header: "Usage",
      muted: true,
      cell: (row) => (row.kind === "drift" ? (row.drift.desired?.purpose ?? "—") : row.pool.purpose),
    },
  ];
}

export function FactoryRunnersPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const writable = isProductionPlane();
  const qc = useQueryClient();

  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [killing, setKilling] = useState<FleetInstance | null>(null);
  const [stopping, setStopping] = useState<string | null>(null);
  // Les instances à terminer avec l'exécution. Rien ne relie une instance à
  // une exécution — ni tag, ni nom — donc le rattachement est fait par
  // l'opérateur. Tout terminer par défaut tuerait les runners des AUTRES
  // exécutions en cours, ce qui ferait échouer leurs jobs sans prévenir.
  const [doomed, setDoomed] = useState<string[]>([]);

  const fleet = useQuery({
    queryKey: ["factory", "fleet"],
    queryFn: () => getFleet(token),
    enabled: !!token,
    // Les instances sont éphémères : sans rafraîchissement la page ment vite.
    // Pas d'actualisation en arrière-plan — l'onglet caché n'a rien à observer.
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
  });

  // Le coût ne bouge que quelques fois par jour et chaque appel est facturé :
  // il se charge une fois, et ne participe pas au polling.
  const cost = useQuery({
    queryKey: ["factory", "fleet", "cost"],
    queryFn: () => getFleetCost(token),
    enabled: !!token,
    staleTime: 6 * 3600_000,
    retry: false,
  });

  // Les deux requêtes suivantes passent par GitHub, dont les quotas sont
  // comptés à l'heure : elles ne participent pas au polling des instances.
  const runners = useQuery({
    queryKey: ["factory", "fleet", "runners"],
    queryFn: () => listRegisteredRunners(token),
    enabled: !!token && writable,
    staleTime: 60_000,
    retry: false,
  });

  const proposals = useQuery({
    queryKey: ["factory", "fleet", "proposals"],
    queryFn: () => listPoolProposals(token),
    enabled: !!token && writable,
    staleTime: 60_000,
    retry: false,
  });

  // Ciblage EXACT. `["factory", "fleet"]` est aussi le préfixe de la requête de
  // coût — Cost Explorer, facturé à l'appel, volontairement hors du polling —
  // et des deux requêtes GitHub, dont le quota se compte à l'heure. Invalider
  // le préfixe rendrait payante et bruyante la terminaison d'une instance.
  const invalidateFleet = () => {
    qc.invalidateQueries({ queryKey: ["factory", "fleet"], exact: true });
  };
  const invalidateRunners = () => {
    qc.invalidateQueries({ queryKey: ["factory", "fleet", "runners"] });
  };
  const invalidateProposals = () => {
    qc.invalidateQueries({ queryKey: ["factory", "fleet", "proposals"] });
  };

  const terminate = useMutation({
    mutationFn: (instanceId: string) => terminateInstance(token, instanceId),
    onSuccess: (result) => {
      setNotice(`Instance ${result.instanceId} terminated.`);
      invalidateFleet();
      // La machine disparue, son enregistrement GitHub devient un fantôme.
      invalidateRunners();
    },
  });

  const stop = useMutation({
    mutationFn: (input: { name: string; instances: string[] }) =>
      stopExecution(token, input.name, input.instances),
    onSuccess: (result) => {
      const killed = result.terminated.filter((t) => t.ok).length;
      setNotice(
        killed > 0
          ? `Execution ${result.execution} stopped, ${killed} instance(s) terminated first.`
          : `Execution ${result.execution} stopped.`,
      );
      invalidateFleet();
      invalidateRunners();
    },
  });

  const rebuild = useMutation({
    mutationFn: (pipeline: string) => rebuildImage(token, pipeline),
    onSuccess: (result) => {
      setNotice(
        `Rebuild of ${result.pipeline} started — allow about twenty minutes.`,
      );
    },
  });

  const deregister = useMutation({
    mutationFn: (target: { repo: string; id: number }) =>
      deregisterRunner(token, target.repo, target.id),
    onSuccess: (result) => {
      setNotice(`Runner ${result.name ?? result.runnerId} deregistered.`);
      invalidateRunners();
    },
  });

  const propose = useMutation({
    mutationFn: (pools: DesiredPool[]) => proposePools(token, pools),
    onSuccess: (result) => {
      setEditing(false);
      setNotice(
        `Proposal opened: ${result.changes.join("; ")}. It's awaiting review, then a fleet deployment.`,
      );
      // Le workflow met une vingtaine de secondes à ouvrir la PR ; ce
      // rafraîchissement peut donc ne rien trouver, et la proposition
      // n'apparaîtra qu'au prochain passage.
      invalidateProposals();
    },
  });

  const data = fleet.data;
  const instances = useMemo(() => data?.instances ?? [], [data]);
  const stale = useMemo(
    () =>
      instances.filter(
        (i) => i.ageSeconds !== null && i.ageSeconds > SUSPICIOUS_AGE_SECONDS,
      ),
    [instances],
  );
  // La vue de dérive vient de `main` quand on peut la lire. Celle de
  // /factory/fleet part de l'instantané du dernier déploiement de l'API, donc
  // elle annonce une fausse dérive tant que celui-ci n'a pas suivi une
  // proposition fusionnée — ce qui ferait passer pour un incident ce qui est
  // simplement un déploiement d'API en retard.
  const pools = useMemo(
    () => proposals.data?.repositoryDrift ?? data?.pools ?? [],
    [data, proposals.data],
  );
  const drifted = useMemo(
    () => pools.filter((p) => p.state !== "in_sync"),
    [pools],
  );
  // La base d'édition vient de `main` quand on peut la lire, et seulement
  // sinon de l'instantané que porte la Lambda de lecture. L'instantané est figé
  // au dernier déploiement de l'API : éditer depuis lui réintroduirait en
  // silence les pools d'une proposition déjà fusionnée.
  const desiredPools = useMemo(
    () =>
      proposals.data?.repositoryPools ??
      pools.map((p) => p.desired).filter((p): p is DesiredPool => p !== null),
    [pools, proposals.data],
  );
  const ghosts = useMemo(
    () => (runners.data?.runners ?? []).filter((r) => r.ghost),
    [runners.data],
  );
  // Un dépôt que le jeton ne peut pas lire ne rend aucun runner — ce qui se
  // lit « aucun fantôme ». Le nommer est la seule façon de distinguer les deux.
  const unreadableRepos = runners.data?.unavailable ?? [];
  // Une seule proposition à la fois : le workflow a une concurrence de 1, et
  // deux propositions ouvertes se contrediraient sans qu'on sache laquelle
  // fusionner. La plus récente fait foi — repérée par le NUMÉRO, qui croît
  // strictement. Prendre la dernière ligne donnerait la plus ancienne : GitHub
  // trie ses PR ouvertes par date de création décroissante.
  const proposal: PoolProposal | null = useMemo(() => {
    const rows = proposals.data?.proposals ?? [];
    return rows.reduce<PoolProposal | null>(
      (best, row) => (best === null || row.number > best.number ? row : best),
      null,
    );
  }, [proposals.data]);
  const proposedByLabel = useMemo(() => {
    const map = new Map<string, DesiredPool>();
    for (const pool of proposal?.pools ?? []) map.set(pool.label, pool);
    return map;
  }, [proposal]);
  // Une proposition illisible rend une liste vide, qui se lirait « tout est
  // supprimé ». La colonne ne s'affiche que quand son contenu veut dire
  // quelque chose ; le bandeau au-dessus dit déjà que la lecture a échoué.
  const showProposed = proposedByLabel.size > 0;
  // Un pool que seule la proposition déclare n'a aucune ligne de dérive : sans
  // ces lignes-là, un ajout serait strictement invisible dans la vue.
  const proposedOnly = useMemo(
    () =>
      (proposal?.pools ?? []).filter(
        (pool) => !(data?.pools ?? []).some((d) => d.label === pool.label),
      ),
    [proposal, data],
  );

  // The declared/deployed pools, then the ones only the proposal declares.
  const poolRows: PoolTableRow[] = [
    ...pools.map((drift): PoolTableRow => ({
      kind: "drift",
      drift,
      proposed: proposedByLabel.get(drift.label) ?? null,
    })),
    ...(showProposed ? proposedOnly.map((pool): PoolTableRow => ({ kind: "proposed", pool })) : []),
  ];

  const busy =
    terminate.isPending ||
    stop.isPending ||
    rebuild.isPending ||
    deregister.isPending;

  return (
    <Page>
      <PageHeader
        section={FACTORY}
        title="Runners"
        subtitle="The fleet of ephemeral machines that runs CI. Each instance serves a single job then disappears — whatever lingers here is costing money without producing anything."
      />

      <div className="space-y-6">
        {!writable && (
          <p className="rounded-md border border-line bg-surface p-3 text-sm text-ink-muted">
            Development environment: read-only. The fleet shown here is the
            real one — dev and production share the same account — but
            actions are performed from the production control plane.
          </p>
        )}

        {notice && (
          <div className="flex items-start justify-between gap-3 rounded-md border border-line bg-surface p-3 text-sm text-ink">
            <span>{notice}</span>
            <Button
              variant="ghost"
              onClick={() => setNotice(null)}
            >
              Close
            </Button>
          </div>
        )}

        {fleet.isLoading && (
          <LoadingRegion className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </LoadingRegion>
        )}
        {fleet.isError && <ErrorBox error={fleet.error} />}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Runners in flight"
                value={instances.length}
                hint={data.stack}
              />
              <StatCard
                label="Abnormally old"
                value={stale.length}
                hint="over 2h — a job doesn't run that long"
                tone={stale.length > 0 ? "warn" : undefined}
              />
              <StatCard
                label="Active runs"
                value={data.executions.running.length}
                hint={`${data.executions.recentFailures.length} recent failure(s)`}
              />
              <StatCard
                label="Cost this month"
                value={
                  cost.isError
                    ? "n/a"
                    : cost.data
                      ? `${cost.data.monthToDateUsd.toFixed(2)} $`
                      : "…"
                }
                hint={
                  cost.data?.measuredAt
                    ? undefined
                    : "EC2 Compute + EC2 Other, same as the budget"
                }
              />
            </div>

            {cost.data?.measuredAt && (
              <p className="text-xs text-ink-muted">
                Cost measured <RelativeTime epochMs={cost.data.measuredAt} />
                {cost.data.cached && " (today's snapshot)"} — Cost Explorer only
                refreshes a few times a day.
              </p>
            )}

            {data.unavailable.length > 0 && (
              <p className="rounded-md border border-line bg-surface p-3 text-sm text-ink">
                Section(s) unreadable on this pass:{" "}
                <span className="font-medium">{data.unavailable.join(", ")}</span>.
                What's shown below is incomplete — it isn't proof that there's
                nothing there.
              </p>
            )}

            {stale.length > 0 && (
              <Notice tone="warning">
                {stale.length} runner(s) in flight for more than two hours. A
                runner only serves a single job: check the associated run
                before concluding it's orphaned.
              </Notice>
            )}

            <div>
              <h2 className="mb-2 text-sm font-semibold text-ink">
                Runners in flight
              </h2>
              <MutationError mutation={terminate} title="Could not terminate the instance" className="mb-2" />
              <Table
                label="Runners in flight"
                columns={INSTANCE_COLUMNS}
                rows={instances}
                rowKey={(instance) => instance.instanceId}
                empty={
                  <EmptyState title="No instances in flight">
                    That&apos;s the normal state between jobs.
                  </EmptyState>
                }
                actions={
                  writable
                    ? {
                        label: (instance) => `Actions for ${instance.instanceId}`,
                        items: (instance) => (
                          <MenuItem danger icon={Power} disabled={busy} onClick={() => setKilling(instance)}>
                            Terminate…
                          </MenuItem>
                        ),
                      }
                    : undefined
                }
              />
            </div>

            {writable && data.executions.running.length > 0 && (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-ink">
                  Active runs
                </h2>
                <p className="mb-2 text-xs text-ink-muted">
                  Stopping a run first terminates any fleet instances still in
                  flight. The reverse order would leave a machine billed for
                  about ten minutes, until the reaper's next pass.
                </p>
                <MutationError mutation={stop} title="Could not stop the run" className="mb-2" />
                <Table
                  label="Active runs"
                  columns={EXECUTION_COLUMNS}
                  rows={data.executions.running}
                  rowKey={executionKey}
                  empty={<EmptyState title="No active runs" />}
                  actions={{
                    label: (execution) => `Actions for ${execution.name ?? "an unnamed run"}`,
                    items: (execution) => (
                      <MenuItem
                        danger
                        icon={Square}
                        disabled={busy || !execution.name}
                        onClick={() => {
                          // Pré-cochées : celles dont l'âge est déjà anormal. Une
                          // instance récente appartient plus probablement à une
                          // autre exécution.
                          setDoomed(stale.map((instance) => instance.instanceId));
                          setStopping(execution.name);
                        }}
                      >
                        Stop…
                      </MenuItem>
                    ),
                  }}
                />
              </div>
            )}

            {writable && unreadableRepos.length > 0 && (
              <p className="rounded-md border border-line bg-surface p-3 text-sm text-ink">
                Runners unreadable on{" "}
                <span className="font-medium">
                  {unreadableRepos.map((r) => r.repo).join(", ")}
                </span>
                . The bootstrap token probably doesn't have this repo in its
                scope. No ghosts will be detected there while that's the case —
                which isn't the same as there being none.
              </p>
            )}

            {writable && ghosts.length > 0 && (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-ink">
                  Ghost runners
                </h2>
                <p className="mb-2 text-xs text-ink-muted">
                  Registered with GitHub even though their machine no longer
                  exists. GitHub keeps assigning them jobs, which stay queued
                  until the 24h timeout — this is the one part of the fleet
                  no AWS console shows.
                </p>
                <MutationError mutation={deregister} title="Could not deregister the runner" className="mb-2" />
                <Table
                  label="Ghost runners"
                  columns={GHOST_COLUMNS}
                  rows={ghosts}
                  rowKey={(runner) => `${runner.repo}#${runner.id}`}
                  empty={<EmptyState title="No ghost runners" />}
                  actions={{
                    label: (runner) => `Actions for ${runner.name}`,
                    items: (runner) => (
                      <MenuItem
                        icon={Unplug}
                        disabled={busy}
                        onClick={() => deregister.mutate({ repo: runner.repo, id: runner.id })}
                      >
                        Deregister
                      </MenuItem>
                    ),
                  }}
                />
              </div>
            )}

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-ink">Pools</h2>
                {writable && !editing && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setEditing(true)}
                    disabled={desiredPools.length === 0}
                  >
                    Edit
                  </Button>
                )}
              </div>
              <p className="mb-2 text-xs text-ink-muted">
                "Desired" is what <code>runner_pools.json</code> declares;
                "deployed" is what the state machine would actually launch
                {showProposed && '; "proposed" is what a pending review is waiting on'}. A
                gap means a fleet deployment is still needed — there's no
                automatic deployment on this repo.
              </p>

              {proposal && (
                <Notice tone="info" className="mb-2">
                  A proposal is open:{" "}
                  <a
                    className="underline"
                    href={proposal.url ?? "#"}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {proposal.title ?? `#${proposal.number}`}
                  </a>
                  . It won't reach the fleet until it's merged, then deployed.
                  {proposal.unreadable && (
                    <>
                      {" "}
                      Its content couldn't be read ({proposal.unreadable}) —
                      the "proposed" column stays empty, which doesn't mean it
                      changes nothing.
                    </>
                  )}
                </Notice>
              )}

              {editing ? (
                <PoolEditor
                  current={desiredPools}
                  pending={propose.isPending}
                  error={<MutationError mutation={propose} title="Could not open the proposal" />}
                  onSubmit={(pools) => propose.mutate(pools)}
                  onCancel={() => {
                    propose.reset();
                    setEditing(false);
                  }}
                />
              ) : (
                <>
                  <Table
                    label="Pools"
                    columns={poolColumns(showProposed)}
                    rows={poolRows}
                    rowKey={(row) => (row.kind === "drift" ? row.drift.label : `proposed-${row.pool.label}`)}
                    empty={<EmptyState title="No pools declared" />}
                  />
                  {drifted.length === 0 && pools.length > 0 && (
                    <p className="mt-2 text-xs text-success-fg">
                      All deployed pools match what's declared.
                    </p>
                  )}
                </>
              )}
            </div>

            {data.images.length > 0 && (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-ink">
                  Runner images
                </h2>
                <p className="mb-2 text-xs text-ink-muted">
                  Rebuilt every week. An image noticeably older than that
                  signals a failed pipeline — jobs keep starting from the old
                  one, so nothing else will tell you.
                </p>
                <MutationError mutation={rebuild} title="Could not start the rebuild" className="mb-2" />
                <Table
                  label="Runner images"
                  columns={IMAGE_COLUMNS}
                  rows={data.images}
                  rowKey={(image, index) => image.name ?? `unnamed-${index}`}
                  empty={<EmptyState title="No runner images" />}
                  actions={
                    writable
                      ? {
                          label: (image) => `Actions for ${image.name ?? "an unnamed pipeline"}`,
                          items: (image) => (
                            <MenuItem
                              icon={Hammer}
                              disabled={busy || !image.name}
                              onClick={() => rebuild.mutate(image.name!)}
                            >
                              Rebuild
                            </MenuItem>
                          ),
                        }
                      : undefined
                  }
                />
              </div>
            )}

            {data.executions.recentFailures.length > 0 && (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-ink">
                  Recent orchestrator failures
                </h2>
                <Table
                  label="Recent orchestrator failures"
                  columns={FAILURE_COLUMNS}
                  rows={data.executions.recentFailures}
                  rowKey={executionKey}
                  empty={<EmptyState title="No recent failures" />}
                />
              </div>
            )}
          </>
        )}

        {killing && (
          <ConfirmModal
            title="Terminate this runner?"
            confirmLabel="Terminate"
            pendingLabel="Terminating…"
            pending={terminate.isPending}
            danger
            onCancel={() => {
              terminate.reset();
              setKilling(null);
            }}
            onConfirm={() => {
              const target = killing;
              setKilling(null);
              terminate.mutate(target.instanceId);
            }}
          >
            <div className="space-y-2">
              <p>
                <span className="font-mono text-xs">{killing.instanceId}</span> —{" "}
                {killing.instanceType ?? "unknown type"}, in flight for{" "}
                {formatAge(killing.ageSeconds)}.
              </p>
              <p>
                If a job is running on it, it will be lost and its run will end
                in failure. A run that's still active will keep waiting: stop
                it from the "Active runs" section instead, which does both in
                the right order.
              </p>
            </div>
          </ConfirmModal>
        )}

        {stopping && (
          <ConfirmModal
            title="Stop this run?"
            confirmLabel={
              doomed.length > 0
                ? `Terminate ${doomed.length} instance(s) then stop`
                : "Stop only"
            }
            pendingLabel="In progress…"
            pending={stop.isPending}
            danger
            onCancel={() => {
              stop.reset();
              setStopping(null);
              setDoomed([]);
            }}
            onConfirm={() => {
              const name = stopping;
              const targets = doomed;
              setStopping(null);
              setDoomed([]);
              stop.mutate({ name, instances: targets });
            }}
          >
            <div className="space-y-2">
              <p>
                <span className="font-mono text-xs">{stopping}</span>
              </p>
              {instances.length > 0 ? (
                <>
                  <p>
                    Nothing links an instance to a run: matching them up is on
                    you. Check the ones this run launched — they'll be
                    terminated <em>before</em> the stop, because the run holds
                    their heartbeat and the reverse order would leave them
                    billed for about ten minutes.
                  </p>
                  <ul className="space-y-1">
                    {instances.map((instance) => {
                      const old =
                        instance.ageSeconds !== null &&
                        instance.ageSeconds > SUSPICIOUS_AGE_SECONDS;
                      return (
                        <li key={instance.instanceId}>
                          <Checkbox
                            label={
                              <>
                                <span className="font-mono text-xs">
                                  {instance.instanceId}
                                </span>
                                <span className="ml-2 text-ink-muted">
                                  {instance.pool ?? "?"} · {formatAge(instance.ageSeconds)}
                                  {old && " · abnormally old"}
                                </span>
                              </>
                            }
                            checked={doomed.includes(instance.instanceId)}
                            onChange={(e) =>
                              setDoomed((ids) =>
                                e.target.checked
                                  ? [...ids, instance.instanceId]
                                  : ids.filter((id) => id !== instance.instanceId),
                              )
                            }
                          />
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : (
                <p>
                  No instances in flight: the run will simply be stopped.
                </p>
              )}
            </div>
          </ConfirmModal>
        )}
      </div>
    </Page>
  );
}
